import React, { useMemo, useRef } from 'react';

import isEqual from 'lodash/isEqual';
import Loadable from 'react-loadable';

import { DefaultExtensionProvider, type Parameters } from '@atlaskit/editor-common/extensions';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	type CustomSitesExtensionProviderProps,
	getExtensionManifest,
} from '@atlassian/company-hub-common';

import {
	SEARCH_EXTENSION_KEY,
	SEARCH_EXTENSION_TYPE,
	SEARCH_EXTENSION_VERSION_1,
} from '../../constants';
import { type CustomSearchExtensionProps } from '../../index';

import { messages } from './messages';

const SearchExtension = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_SearchExtension" */ '../custom-search-extension'
			)
		).CustomSearchExtension,
	loading: () => <></>,
});

const SearchExtensionContainerStyles = xcss({
	// This padding ensures that the corners of the search bar are not cut off by the containers border
	// radius when the search bar has square corners.
	padding: 'space.025',
});

export const searchExtensionProvider = ({
	mode,
	contentId,
	propsProvider,
}: CustomSearchExtensionProps) => {
	return async ({
		intl,
		editorActions,
		openConfigPanel,
		setUpsellModalSource,
	}: CustomSitesExtensionProviderProps): Promise<DefaultExtensionProvider<Parameters>> => {
		return new DefaultExtensionProvider([
			getExtensionManifest({
				key: SEARCH_EXTENSION_KEY,
				type: SEARCH_EXTENSION_TYPE,
				// Temporal title to distinguish platformized custom search
				title: intl.formatMessage(messages.title),
				description: intl.formatMessage(messages.description),
				source: 'premiumSearchInsert',
				setUpsellModalSource,
				icons: {
					'48': async () =>
						(
							await import(
								/* webpackChunkName: "@atlaskit-internal_SearchExtensionIcon" */
								'./icons/search-extension-icon'
							)
						).SearchExtensionIcon,
				},

				parameters: {
					extensionTitle: intl.formatMessage(messages.title),
					version: SEARCH_EXTENSION_VERSION_1,
					filters: {
						product: 'confluence' as const,
					},
					styles: {
						placeholder: intl.formatMessage(messages.defaultPlaceholderText),
						backgroundColor: token('elevation.surface.overlay'),
						textColor: token('color.text'),
						borderRadius: token('radius.large'),
					},
				},

				update: async () => {
					const selectedNode = editorActions?.getSelectedNode()?.toJSON();
					const selectedLocalId = selectedNode?.attrs?.localId;

					if (Boolean(selectedLocalId) && openConfigPanel) {
						openConfigPanel(selectedLocalId);
					}
				},

				render:
					async () =>
					({ node }: { node: any }) => {
						// We want to avoid re-rendering the SearchInput from the platform package
						// to prevent issues related to its state logic. For example, the store
						// doesn’t check for the initial value in the text field, leading to
						// incorrect responses in the dropdown menu.
						const prevNodeRef = useRef(node);
						const extensionNode = useMemo(() => {
							if (!isEqual(prevNodeRef.current, node)) {
								prevNodeRef.current = node;
							}
							return prevNodeRef.current;
						}, [node]);

						return (
							<Box xcss={SearchExtensionContainerStyles}>
								<SearchExtension
									mode={mode}
									contentId={contentId}
									extensionNode={extensionNode}
									propsProvider={propsProvider}
								/>
							</Box>
						);
					},
			}),
		]);
	};
};
